"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { trackSectionView, trackScrollDepth } from "@/lib/analytics";

// Page-level engagement instrumentation, mounted once in the locale layout so it
// covers every route, not just the home page. Several sections outside the home
// page carry data-track-section (the whole /tarieven page, for one), and while
// this lived in app/[locale]/page.tsx none of them ever reported.
//   - section_viewed: the first time each [data-track-section] element is >=30%
//     visible (so an element already on screen at load, e.g. the hero, counts).
//   - scroll_depth: once per 25/50/75/100% milestone of the page's scrollable
//     height.
// Keyed on pathname rather than mounted once: the layout survives client-side
// navigation, so without the key a route the visitor navigates to would never be
// scanned. One effect run == one page view's worth of events. track() itself
// no-ops the PostHog leg until consent + load, so this is consent-safe.
const SCROLL_MILESTONES = [25, 50, 75, 100] as const;

export function PageEngagement() {
  const pathname = usePathname();

  useEffect(() => {
    // --- Section views ---
    let observer: IntersectionObserver | undefined;
    const sections = document.querySelectorAll<HTMLElement>("[data-track-section]");
    if (sections.length > 0 && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries, obs) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const name = (entry.target as HTMLElement).dataset.trackSection;
            if (name) trackSectionView(name);
            obs.unobserve(entry.target);
          }
        },
        { threshold: 0.3 },
      );
      sections.forEach((el) => observer!.observe(el));
    }

    // --- Scroll depth ---
    const fired = new Set<number>();
    let ticking = false;
    const measure = () => {
      ticking = false;
      const scrollable =
        document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const pct = (window.scrollY / scrollable) * 100;
      for (const milestone of SCROLL_MILESTONES) {
        if (pct >= milestone && !fired.has(milestone)) {
          fired.add(milestone);
          trackScrollDepth(milestone);
        }
      }
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    measure(); // a short page may already be past a milestone on load

    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", onScroll);
    };
  }, [pathname]);

  return null;
}
